import printJS from "print-js";
import { type ComponentPropsWithoutRef, useCallback, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/lib/api";
import { isMobileOrTablet } from "@/utils/detection";

type PrintWorklistsButtonProps = ComponentPropsWithoutRef<typeof Button> & {
  run: { id: number; createdAt?: Date | null };
  worklists: { id: number }[];
};

export function PrintWorklistsButton({
  run,
  worklists,
  ...props
}: PrintWorklistsButtonProps) {
  const testId = worklists.length === 1 ? worklists[0].id : undefined;
  const { print, loading } = usePrintWorklistsPdf({ run, worklists });
  const shouldShow = !isMobileOrTablet();

  return shouldShow ? (
    <Button
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        print(testId);
      }}
      isPending={props.isPending || loading}
    />
  ) : null;
}

export function usePrintWorklistsPdf({
  run,
  worklists,
  onPrintDialogClose,
}: PrintWorklistsButtonProps & {
  onPrintDialogClose?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const testId = worklists.length === 1 ? worklists[0].id : undefined;
  const print = useCallback(
    (testIdOverride?: number) => {
      const src = getApiUrl(
        `/api/worklist/${run.id}${testIdOverride ? `/${testIdOverride}` : testId ? `/${testId}` : ""}/pdf`,
      );
      printJS({
        type: "pdf",
        printable: src,
        onLoadingStart: () => {
          setLoading(true);
        },
        onLoadingEnd: () => {
          setLoading(false);
        },
        onError: (error) => {
          setLoading(false);
          toast.error("Failed to print worklists", {
            description: error.message,
          });
        },
        onPrintDialogClose: () => {
          onPrintDialogClose?.();
        },
      });
    },
    [run.id, testId, onPrintDialogClose],
  );
  return { print, loading };
}
